import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import TechnologieCard from "../../components/quizz/TechnologieCard";
import SearchBar from "../../components/quizz/SeachBar";
import CategoryFilter from "../../components/quizz/CategoryFilter";
import { DEFAULT_ROUTE } from "../../routes/api/api_routes";

const TechnologieBrowser = ({ onTechSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [technos, setTechnos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("access");

        const [techRes, catRes] = await Promise.all([
          fetch(`${DEFAULT_ROUTE}technology/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${DEFAULT_ROUTE}category/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        const techData = await techRes.json();
        const catData = await catRes.json();

        setTechnos(techData.data);
        setCategories(catData.data);
      } catch (err) {
        setError("Impossible de charger les données");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredTech = technos.filter((t) => {
    const matchCategory =
      selectedCategory === "all" || t.category === selectedCategory;
    const matchSearch =
      t.name && t.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  if (isLoading) {
    return (
      <div className="px-5 py-8 mx-auto w-full">
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2">
          Parcourez les technologies disponibles
        </h1>
        <p className="text-gray-600 text-center mb-6 max-w-2xl mx-auto">
          Choisissez une technologie pour commencer un quiz adapté à votre
          niveau.
        </p>
        <SearchBar onSearch={setSearchTerm} />
        <h2 className="text-xl font-semibold text-center my-4">
          Filtrer par catégorie
        </h2>
        <div className="flex flex-wrap gap-4 mt-4 justify-center max-w-5xl mx-auto my-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-gray-200 px-4 py-2 rounded-full border"
            ></div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-200 rounded-2xl h-40 w-full"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-4 p-3 bg-red-100 border border-red-300 text-red-700 text-center rounded-md shadow-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="px-5 py-8 mx-auto w-full">
      <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2">
        Parcourez les technologies disponibles
      </h1>
      <p className="text-gray-600 text-center mb-6 max-w-2xl mx-auto">
        Choisissez une technologie pour commencer un quiz adapté à votre niveau.
      </p>

      {/* 🔹 Barre de recherche sur desktop */}
      <div className="hidden sm:block">
        <SearchBar onSearch={setSearchTerm} />
      </div>

      <h2 className="text-xl font-semibold text-center my-4">
        Filtrer par catégorie
      </h2>
      <CategoryFilter
        categories={categories}
        selected={selectedCategory}
        onSelect={setSelectedCategory}
      />

      {/* 🔹 Barre de recherche sur mobile (sous les catégories) */}
      <div className="block sm:hidden mt-4">
        <SearchBar onSearch={setSearchTerm} />
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 place-items-center items-stretch">
        <AnimatePresence>
          {filteredTech.map((tech, index) => (
            <motion.div
              key={tech.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <TechnologieCard
                techno={tech}
                onSelect={() => onTechSelect(tech.id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {filteredTech.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="col-span-full flex flex-col items-center justify-center py-2"
          >
            {/* Icône */}
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="p-4 bg-orange-100 rounded-full"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-12 h-10 text-orange-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>
            </motion.div>

            {/* Texte animé */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-2 text-gray-600 text-lg font-medium"
            >
              Aucune technologie trouvée
            </motion.p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default TechnologieBrowser;
